import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";

const EmptyChart = () => {
  const svgRef = useRef();
  const containerRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width } = entry.contentRect;

        setDimensions({
          width: width,
          height: width / 3,
        });
      }
    });
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    d3.select(svgRef.current).selectAll("*").remove();

    const margin = { top: 10, right: 50, bottom: 30, left: 50 };
    const width = dimensions.width - margin.left - margin.right;
    const height = dimensions.height - margin.top - margin.bottom;

    const svg = d3
      .select(svgRef.current)
      .attr("viewBox", `0 0 ${dimensions.width} ${dimensions.height}`)
      .style("width", "100%")
      .style("height", "100%");

    const xScale = d3.scaleBand().domain([]).range([0, width]).padding(0.2);
    const yScale = d3.scaleLinear().domain([0, 100]).range([height, 0]).nice();

    const boxWidth = 200;
    const boxHeight = 60;

    // Draw light gray background box
    svg
      .append("rect")
      .attr("x", (dimensions.width - boxWidth) / 2)
      .attr("y", (dimensions.height - boxHeight) / 2)
      .attr("width", boxWidth)
      .attr("height", boxHeight)
      .attr("rx", 10) // optional: rounded corners
      .attr("fill", "#f5f5f5");

    // Add "No data found" text centered in the box
    svg
      .append("text")
      .attr("x", dimensions.width / 2)
      .attr("y", dimensions.height / 2)
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle")
      .style("fill", "#555") // dark gray text
      .style("font-size", "16px")
      .text("No data to display");

    const fontSize = Math.max(10, Math.min(16, width / 50));

    const yTicks = Math.max(5, Math.floor(height / 50));

    // Grid
    svg
      .append("g")
      .attr("class", "grid y-grid")
      .attr("transform", `translate(${margin.left}, ${margin.top})`)
      .call(d3.axisLeft(yScale).ticks(yTicks).tickSize(-width).tickFormat(""))
      .call((g) => g.selectAll(".domain").remove())
      .call((g) => g.selectAll("line").attr("stroke", "#e0e0e0"));

    svg
      .append("g")
      .attr("class", "grid x-grid")
      .attr("transform", `translate(${margin.left}, ${height + margin.top})`)
      .call(d3.axisBottom(xScale).tickSize(-height).tickFormat(""))
      .call((g) => g.selectAll(".domain").remove())
      .call((g) => g.selectAll("line").attr("stroke", "#e0e0e0"));

    svg
      .append("g")
      .attr("class", "grid y-grid-right")
      .attr("transform", `translate(${width + margin.left}, ${margin.top})`)
      .call(d3.axisRight(yScale).tickSize(0).tickFormat(""))
      .call((g) => g.selectAll(".domain").attr("stroke", "#e0e0e0"))
      .call((g) => g.selectAll(".tick").remove());

    // Axes
    svg
      .append("g")
      .attr("class", "x-axis")
      .attr("transform", `translate(${margin.left}, ${height + margin.top})`)
      .call(d3.axisBottom(xScale))
      .selectAll("text")
      .style("text-anchor", "middle")
      .attr("fill", "gray")
      .style("font-size", `${fontSize}px`);

    svg.select(".x-axis path").attr("stroke", "#e0e0e0");
    svg.select(".x-axis").selectAll(".tick line").attr("stroke", "#e0e0e0");

    svg
      .append("g")
      .attr("class", "y-axis")
      .attr("transform", `translate(${margin.left}, ${margin.top})`)
      .call(
        d3
          .axisLeft(yScale)
          .ticks(yTicks)
          .tickFormat((d) => `${d}%`)
      )
      .selectAll("text")
      .attr("fill", "gray")
      .style("font-size", `${fontSize}px`);

    svg.select(".y-axis path").attr("stroke", "#e0e0e0");
    svg.select(".y-axis").selectAll(".tick line").attr("stroke", "#e0e0e0");
  }, [dimensions]);

  return (
    <div
      className="mt-5"
      ref={containerRef}
      style={{ width: "100%", minHeight: "300px" }}
    >
      <svg ref={svgRef} />
    </div>
  );
};

export default EmptyChart;
